/**
 * TypeScript types for the Guardrails module (api/guardrails).
 * Enums are NUMERIC on the wire (persisted as TINYINT, ordinal base 1 unless noted).
 */

/** GuardrailAction ordinals (Domain/Enums/Guard/GuardrailAction.cs). */
export const GuardrailAction = {
  Allow: 1,
  Flag: 2,
  Redact: 3,
  Tokenize: 4,
  Rewrite: 5,
  Regenerate: 6,
  Block: 7,
  RequireApproval: 8,
  EscalateModel: 9,
  TerminateSession: 10,
  Neutralize: 11,
  Deflect: 12,
  Escalate: 13,
} as const;
export type GuardrailAction = typeof GuardrailAction[keyof typeof GuardrailAction];

export const guardrailActionLabels: Record<number, string> = {
  1: 'Allow',
  2: 'Flag',
  3: 'Redact',
  4: 'Tokenize',
  5: 'Rewrite',
  6: 'Regenerate',
  7: 'Block',
  8: 'Require approval',
  9: 'Escalate model',
  10: 'Terminate session',
  11: 'Neutralize',
  12: 'Deflect',
  13: 'Escalate',
};

/** GuardrailMode ordinals. */
export const GuardrailMode = {
  Shadow: 1,
  Enforce: 2,
} as const;
export type GuardrailMode = typeof GuardrailMode[keyof typeof GuardrailMode];

export const guardrailModeLabels: Record<number, string> = {
  1: 'Shadow',
  2: 'Enforce',
};

/** GuardrailDirection ordinals. v1 bindings admit UserInput (1) and AgentOutput (7) only. */
export const GuardrailDirection = {
  UserInput: 1,
  ContextAssembled: 2,
  ModelOutput: 3,
  ToolCall: 4,
  ToolResult: 5,
  AgentHandoff: 6,
  AgentOutput: 7,
  Paired: 8,
} as const;
export type GuardrailDirection = typeof GuardrailDirection[keyof typeof GuardrailDirection];

export const guardrailDirectionLabels: Record<number, string> = {
  1: 'User input',
  2: 'Context assembled',
  3: 'Model output',
  4: 'Tool call',
  5: 'Tool result',
  6: 'Agent handoff',
  7: 'Agent output',
  8: 'Paired',
};

/** GuardrailSafetyTier ordinals (base 0). */
export const GuardrailSafetyTier = {
  Tier0: 0,
  Tier1: 1,
  Standard: 2,
} as const;
export type GuardrailSafetyTier = typeof GuardrailSafetyTier[keyof typeof GuardrailSafetyTier];

export const guardrailSafetyTierLabels: Record<number, string> = {
  0: 'Tier 0',
  1: 'Tier 1',
  2: 'Standard',
};

/** GuardrailStreamingStrategy ordinals. */
export const GuardrailStreamingStrategy = {
  BufferFull: 1,
  ChunkedWindow: 2,
  OptimisticRetract: 3,
} as const;
export type GuardrailStreamingStrategy =
  typeof GuardrailStreamingStrategy[keyof typeof GuardrailStreamingStrategy];

/** FailMode ordinals (Domain/Enums/Harness/FailMode.cs). */
export const FailMode = {
  FailOpen: 1,
  FailClosed: 2,
} as const;
export type FailMode = typeof FailMode[keyof typeof FailMode];

export const failModeLabels: Record<number, string> = {
  1: 'Fail open',
  2: 'Fail closed',
};

/** LifecycleStatus ordinals (Domain/Enums/Harness/LifecycleStatus.cs). */
export const LifecycleStatus = {
  Draft: 1,
  Active: 2,
  Deprecated: 3,
  Retired: 4,
} as const;
export type LifecycleStatus = typeof LifecycleStatus[keyof typeof LifecycleStatus];

export const lifecycleStatusLabels: Record<number, string> = {
  1: 'Draft',
  2: 'Active',
  3: 'Deprecated',
  4: 'Retired',
};

/** GuardrailVerdict ordinals — what the detector FOUND. */
export const GuardrailVerdict = {
  Pass: 1,
  Violation: 2,
  Error: 3,
  Skipped: 4,
  Inconclusive: 5,
} as const;
export type GuardrailVerdict = typeof GuardrailVerdict[keyof typeof GuardrailVerdict];

export const guardrailVerdictLabels: Record<number, string> = {
  1: 'Pass',
  2: 'Violation',
  3: 'Error',
  4: 'Skipped',
  5: 'Inconclusive',
};

/** GuardrailOutcome ordinals — what the platform DID. */
export const GuardrailOutcome = {
  Allowed: 1,
  Modified: 2,
  Blocked: 3,
  PendingApproval: 4,
  Escalated: 5,
  Observed: 6,
  Exempted: 7,
  Terminated: 8,
} as const;
export type GuardrailOutcome = typeof GuardrailOutcome[keyof typeof GuardrailOutcome];

export const guardrailOutcomeLabels: Record<number, string> = {
  1: 'Allowed',
  2: 'Modified',
  3: 'Blocked',
  4: 'Pending approval',
  5: 'Escalated',
  6: 'Observed',
  7: 'Exempted',
  8: 'Terminated',
};

/** Guardrail severity strings (persisted as-is). */
export type GuardrailSeverity = 'Low' | 'Medium' | 'High' | 'Critical';

/** One Guard.Guardrails version row. */
export interface GuardrailDto {
  id: string;
  tenantId: string;
  projectId?: string | null;
  guardrailKey: string;
  version: number;
  isCurrent: boolean;
  name: string;
  description?: string | null;
  typeKey: string;
  configJson: string;
  configHash: string;
  defaultViolationAction: number;
  defaultInconclusiveAction?: number | null;
  defaultErrorAction?: number | null;
  defaultSeverity: GuardrailSeverity;
  defaultFailMode: number;
  defaultTimeoutMs: number;
  safetyTier: number;
  isNonSuppressible: boolean;
  minRecallTarget?: number | null;
  status: number;
  owner: string;
  approvedBy?: string | null;
  approvedByRef?: string | null;
  approvedAtUtc?: string | null;
  effectiveFromUtc?: string | null;
  validationEvalRunId?: string | null;
  changeReason?: string | null;
  validationReportRef?: string | null;
  suiteRef?: string | null;
  tagsJson?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One Guard.GuardrailBindings revision row. */
export interface GuardrailBindingDto {
  id: string;
  tenantId: string;
  projectId?: string | null;
  bindingKey: string;
  revision: number;
  isCurrent: boolean;
  bindingHash: string;
  guardrailId: string;
  guardrailKey: string;
  tracksCurrent: boolean;
  agentDefinitionId?: string | null;
  direction: number;
  mode: number;
  executionOrder: number;
  violationAction: number;
  inconclusiveAction: number;
  errorAction: number;
  severity: GuardrailSeverity;
  timeoutMs: number;
  streamingStrategy: number;
  isNonSuppressible: boolean;
  exemptionsAllowed: boolean;
  isActive: boolean;
  owner?: string | null;
  approvedBy?: string | null;
  approvedByRef?: string | null;
  approvedAtUtc?: string | null;
  validationEvalRunId?: string | null;
  effectiveFromUtc: string;
  effectiveUntilUtc?: string | null;
  changeReason?: string | null;
  overrideJson?: string | null;
  agentSafetyGateId?: string | null;
  createdAt: string;
}

/** One Guard.GuardrailExecutions row (subset relevant to the UI; extra fields pass through). */
export interface GuardrailExecutionDto {
  id: string;
  stageCorrelationId: string;
  stageSequence: number;
  isDecisive: boolean;
  shortCircuited: boolean;
  stageDurationMs?: number | null;
  agentDefinitionId?: string | null;
  agentSessionId?: string | null;
  agentExecutionId?: string | null;
  toolExecutionId?: string | null;
  traceId?: string | null;
  principalRef?: string | null;
  locale?: string | null;
  guardrailBindingId?: string | null;
  bindingKey?: string | null;
  bindingRevision?: number | null;
  bindingHash?: string | null;
  guardrailId?: string | null;
  guardrailKey?: string | null;
  guardrailVersion?: number | null;
  typeKey?: string | null;
  configHash?: string | null;
  policySetHash: string;
  direction: number;
  mode: number;
  executionOrder: number;
  verdict: number;
  score?: number | null;
  threshold?: number | null;
  matchCount: number;
  resultJson?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  timedOut: boolean;
  inconclusiveReason?: number | null;
  wasSkipped: boolean;
  skipReason?: number | null;
  outcome: number;
  actionTaken: number;
  actionApplied: boolean;
  failModeApplied?: number | null;
  attemptIndex: number;
  severity: GuardrailSeverity;
  mutationJson?: string | null;
  outcomeJson?: string | null;
  agentSafetyGateExecutionId?: string | null;
  contentHash: string;
  cacheHit: boolean;
  durationMs: number;
  costUsd: number;
  llmModelId?: string | null;
  detectorJson?: string | null;
  occurredAtUtc: string;
  recordHash: string;
  previousRecordHash?: string | null;
}

/** One GR-IN-155 unguarded-turn sentinel. */
export interface UnguardedTurnDto {
  id: string;
  stageCorrelationId: string;
  agentDefinitionId?: string | null;
  agentSessionId?: string | null;
  agentExecutionId?: string | null;
  direction: number;
  occurredAtUtc: string;
}

/** A registered detector's capability declaration (GET api/guardrails/detectors/{typeKey}/schema). */
export interface DetectorInfoDto {
  typeKey: string;
  family: number;
  primitive: number;
  stages: number[];
  actions: number[];
  modalities: number[];
  streaming: number;
  returnsSpans: boolean;
  multiTurn: boolean;
  languages: string[];
  costClass: number;
  latencyClass: number;
  determinism: number;
  schemaJson?: string | null;
}

/** POST api/guardrails — publish a new Draft version. */
export interface PublishGuardrailVersionRequest {
  guardrailKey: string;
  name: string;
  description?: string;
  typeKey: string;
  configJson: string;
  defaultViolationAction: number;
  defaultInconclusiveAction?: number;
  defaultErrorAction?: number;
  defaultSeverity: GuardrailSeverity;
  defaultFailMode: number;
  defaultTimeoutMs: number;
  safetyTier: number;
  isNonSuppressible: boolean;
  minRecallTarget?: number;
  owner: string;
  tagsJson?: string;
  changeReason?: string;
}

/** POST api/guardrails/{key}/versions/{version}/promote. */
export interface PromoteGuardrailVersionRequest {
  approvedBy: string;
  approvedByRef?: string;
  validationEvalRunId: string;
  changeReason?: string;
}

/** POST api/guardrails/{key}/versions/{version}/retire. */
export interface RetireGuardrailVersionRequest {
  changeReason: string;
}

/** POST api/guardrails/bindings — create a new binding revision. */
export interface CreateGuardrailBindingRequest {
  bindingKey?: string;
  guardrailKey: string;
  tracksCurrent: boolean;
  guardrailId?: string;
  agentDefinitionId?: string;
  direction: number;
  mode: number;
  executionOrder: number;
  violationAction: number;
  inconclusiveAction: number;
  errorAction: number;
  severity: GuardrailSeverity;
  timeoutMs: number;
  streamingStrategy: number;
  isNonSuppressible: boolean;
  exemptionsAllowed: boolean;
  owner?: string;
  approvedBy?: string;
  approvedByRef?: string;
  agentSafetyGateId?: string;
  effectiveFromUtc?: string;
  changeReason?: string;
}

/** POST api/guardrails/bindings/{bindingKey}/mode. */
export interface SetGuardrailBindingModeRequest {
  mode: number;
  approvedBy?: string;
  approvedByRef?: string;
  changeReason: string;
}

/** POST api/guardrails/bindings/{bindingKey}/deactivate. */
export interface DeactivateGuardrailBindingRequest {
  changeReason: string;
}

/** POST api/guardrails/bindings/{bindingKey}/reactivate. */
export interface ReactivateGuardrailBindingRequest {
  changeReason: string;
  approvedBy?: string;
  approvedByRef?: string;
}

/** Filters for GET api/guardrails. */
export interface GuardrailFilters {
  typeKey?: string;
  status?: number;
}
